import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { InvestmentRoundingSetting, roundInvestmentShares } from 'shared';

import { CalendarIcon } from '../../../../assets/icons/index';
import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../components/date-picker/index';
import { FormInput } from '../../../../components/input/index';
import { useFormat, useLocale } from '../../../../hooks/index';
import { EventFormType, EventUnionWithPermissions } from '../../../../types/events.types';
import { toNumber } from '../../../../utils/to-number.util';
import {
  FormDropDown,
  ShareClassCombobox,
  StakeholderCombobox,
  SubEventFileUpload,
} from '../components/index';
import { FormSchema, MAX_FUNDRAISING_INVESTMENT_ITEMS } from '../event-form.validation';
import { resolveTransactionItemDocusignFileLinks } from '../utils/resolve-linked-signature-file-links.util';

export type FundraisingRoundOptionalProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  errors: FieldErrors<FormSchema>;
  event: EventUnionWithPermissions | null;
  formData: UseFormGetValues<FormSchema>;
  lockMode?: boolean;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const FundraisingRoundOptionalStep: FC<FundraisingRoundOptionalProps> = ({
  control,
  setFormData,
  companyId,
  lockMode,
  formData,
  setError,
  clearErrors,
  errors,
  event,
}) => {
  const { initialStep, stepOne, optionalStep } = useWatch<FormSchema>({ control });

  const defaultDate = useMemo(() => new Date(+0), []);

  const {
    fields: investmentItemsFields,
    append: investmentItemsAppend,
    remove: investmentItemsRemove,
  } = useFieldArray({
    control,
    name: 'optionalStep.fundraising-round.investmentItems',
  });

  const handleRemoveInvestmentItems = useCallback(
    (index: number) => {
      investmentItemsRemove(index);
    },
    [investmentItemsRemove],
  );

  const handleAppendInvestmentItems = useCallback(() => {
    investmentItemsAppend({
      investmentDate: defaultDate,
      sharePrice: 0,
      shareClass: { id: '', name: '' },
      stakeholder: { id: '', fullName: '' },
      investment: 0,
    });
  }, [defaultDate, investmentItemsAppend]);

  useEffect(() => {
    if (
      initialStep?.type === EventFormType.FUNDRAISING_ROUND &&
      investmentItemsFields.length === 0
    ) {
      handleAppendInvestmentItems();
    }
  }, [handleAppendInvestmentItems, initialStep?.type, investmentItemsFields.length]);

  const fundraisingRoundDetails = stepOne?.eventDetails?.['fundraising-round'];
  const fundraisingRoundIsOpen = fundraisingRoundDetails?.isOpen;
  const fundraisingRoundCloseDate = fundraisingRoundDetails?.closeDate;
  const optionalFundraisingCloseDate = optionalStep?.['fundraising-round']?.closeDate;

  useEffect(() => {
    if (lockMode) {
      return;
    }

    // Mirror the closing date the user picked in step one — this used to copy the round's opening
    // date, so the investment-date bound below was wrong whenever the two differed.
    const closeDate = fundraisingRoundIsOpen ? undefined : fundraisingRoundCloseDate;
    const closeDateTime = closeDate?.getTime();
    const optionalCloseDateTime = optionalFundraisingCloseDate?.getTime();

    if (closeDateTime !== optionalCloseDateTime) {
      setFormData('optionalStep.fundraising-round.closeDate', closeDate);
    }
  }, [
    fundraisingRoundCloseDate,
    fundraisingRoundIsOpen,
    lockMode,
    optionalFundraisingCloseDate,
    setFormData,
  ]);

  return (
    <div className="flex flex-col gap-6 px-6">
      <div className="w-fit rounded bg-gray-100 px-2 py-1">
        <span className="text-sm font-[450] text-gray-500">
          {stepOne?.eventDetails?.['fundraising-round']?.event?.name}
        </span>
      </div>
      {investmentItemsFields.map((field, index) => (
        <InvestmentItem
          clearErrors={clearErrors}
          companyId={companyId}
          control={control}
          errors={errors}
          event={event}
          formData={formData}
          handleRemoveInvestmentItems={handleRemoveInvestmentItems}
          index={index}
          key={field.id}
          setError={setError}
          setFormData={setFormData}
          showRemoveButton={investmentItemsFields.length > 1}
        />
      ))}
      <Button
        className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
        disabled={investmentItemsFields.length >= MAX_FUNDRAISING_INVESTMENT_ITEMS}
        onClick={handleAppendInvestmentItems}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_ANOTHER_INVESTMENT" />
      </Button>
    </div>
  );
};

const InvestmentItem: FC<
  {
    handleRemoveInvestmentItems: (index: number) => void;
    index: number;
    showRemoveButton: boolean;
  } & FundraisingRoundOptionalProps
> = ({
  control,
  setFormData,
  companyId,
  index,
  handleRemoveInvestmentItems,
  showRemoveButton,
  clearErrors,
  errors: { optionalStep: errors },
  formData,
  setError,
  event,
}) => {
  const { stepOne, optionalStep } = useWatch<FormSchema>({ control });
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const defaultDate = new Date(+0);

  const getFullTitle = useCallback(
    (index: number) => {
      const stakeholderName =
        optionalStep?.['fundraising-round']?.investmentItems?.[index]?.stakeholder?.fullName;

      if (stakeholderName) {
        return `${messagesLocale['INVESTMENT']} - ${stakeholderName}`;
      }

      return messagesLocale['INVESTMENT'];
    },
    [messagesLocale, optionalStep],
  );

  const calculateShares = useCallback(
    (index: number) => {
      const investment = toNumber(
        optionalStep?.['fundraising-round']?.investmentItems?.[index]?.investment,
      );

      const sharePrice = toNumber(stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice);

      const roundingSetting =
        stepOne?.eventDetails?.['fundraising-round']?.roundingSetting ??
        InvestmentRoundingSetting.DEFAULT;

      if (investment && sharePrice) {
        return roundInvestmentShares(investment, sharePrice, roundingSetting);
      }

      return 0;
    },
    [optionalStep, stepOne?.eventDetails],
  );

  const isSharesInteger = Number.isInteger(calculateShares(index));

  const fundraisingSharePrice = stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice;
  const investmentItemSharePrice =
    optionalStep?.['fundraising-round']?.investmentItems?.[index]?.sharePrice;

  useEffect(() => {
    if (fundraisingSharePrice === undefined || fundraisingSharePrice === investmentItemSharePrice) {
      return;
    }

    setFormData(
      `optionalStep.fundraising-round.investmentItems.${index}.sharePrice`,
      fundraisingSharePrice,
    );
  }, [fundraisingSharePrice, index, investmentItemSharePrice, setFormData]);

  return (
    <FormDropDown
      handleRemove={() => handleRemoveInvestmentItems(index)}
      showRemoveButton={showRemoveButton}
      title={getFullTitle(index)}
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="STAKEHOLDER" />
            </span>
            <StakeholderCombobox
              companyId={companyId}
              control={control}
              name={`optionalStep.fundraising-round.investmentItems.${index}.stakeholder`}
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="INVESTMENT" />
            </span>
            <FormInput
              control={control}
              currencySign
              errorMessage={!isSharesInteger && 'Investment must be a multiple of share price'}
              maxDecimalPlaces={2}
              name={`optionalStep.fundraising-round.investmentItems.${index}.investment`}
              numberOnly
              placeholder={<AppFormattedMessage id="VALUE" />}
              shouldFormatNumber
              wrapperClassName="w-[224px]"
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARE_CLASS" />
            </span>
            <ShareClassCombobox
              companyId={companyId}
              control={control}
              fieldName={`optionalStep.fundraising-round.investmentItems.${index}.shareClass`}
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="INVESTMENT_DATE" />
            </span>
            <DatePicker
              calendar={{ toDate: optionalStep?.['fundraising-round']?.closeDate }}
              onSelect={(date) =>
                setFormData(
                  `optionalStep.fundraising-round.investmentItems.${index}.investmentDate`,
                  date,
                )
              }
              value={optionalStep?.['fundraising-round']?.investmentItems?.[index]?.investmentDate}
              wrapperClassName="w-[224px]"
            >
              <FormInput
                autoComplete="off"
                control={control}
                disabledInput
                icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
                name={`optionalStep.fundraising-round.investmentItems.${index}.investmentDate`}
                placeholder={<AppFormattedMessage id="DATE" />}
                transform={(value) => {
                  return value instanceof Date && value.getTime() !== defaultDate.getTime()
                    ? format(value, 'dd/MM/yyyy')
                    : '';
                }}
                wrapperClassName="w-full"
              />
            </DatePicker>
          </div>
          <div className="h-[1px] w-full bg-gray-100" />
          <div className="flex w-full items-center justify-between pb-1 pt-3">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="NO_OF_SHARES" />
            </span>
            <span className="rounded bg-brand-25 px-3 py-1 text-sm font-[550] text-brand-700">
              <AnimatedNumber integer value={calculateShares(index)} />
            </span>
          </div>
        </div>
        <SubEventFileUpload
          clearErrors={clearErrors}
          companyId={companyId}
          errors={errors?.['fundraising-round']?.investmentItems?.[index]?.files || undefined}
          field={`optionalStep.fundraising-round.investmentItems.${index}.files`}
          files={optionalStep?.['fundraising-round']?.investmentItems?.[index]?.files || []}
          formData={formData}
          linkedDocusignFileLinks={resolveTransactionItemDocusignFileLinks(
            event,
            optionalStep?.['fundraising-round']?.investmentItems?.[index]?.id,
          )}
          setError={setError}
          setFormData={setFormData}
          text="investment"
        />
      </div>
    </FormDropDown>
  );
};
